import dayjs from 'dayjs';
import invariant from 'tiny-invariant';
import { useTranslation } from 'translations/hooks';

import { useRestApiHeaders } from 'business/contextProviders/useApiHeaders';
import { useAppContext } from 'business/contextProviders/useAppContext';
import { DASHBOARD_REFRESH_TIMEOUT_IN_MINUTES } from 'business/dashboard/constants';
import { usePerformanceMetricsControllerGetPerformanceMetrics } from 'generated/apiComponents';
import { Module_Enum } from 'generated/graphql';
import {
  HUMAN_READABLE_FORMAT,
  MONTH_YEAR_FORMAT,
} from 'technical/string/formatter';
import { humanizeDuration } from 'technical/time-utils/humanize-duration';
import { minutesToMilliseconds } from 'technical/time-utils/minutes-to-milliseconds';
import { isUndefinedOrNull } from 'technical/utils/is-null-or-undefined';

export const usePerformanceMetrics = () => {
  // No need of the return functions, just to watch the local changes
  const { t } = useTranslation();
  const { currentConstructionSite } = useAppContext();
  const restHeaders = useRestApiHeaders(Module_Enum.Dashboard);

  invariant(currentConstructionSite, 'No constructionSite id');

  const {
    data: buildPerformance,
    isFetching: buildPerformanceLoading,
    error: buildPerformanceError,
  } = usePerformanceMetricsControllerGetPerformanceMetrics(
    {
      queryParams: {
        constructionSiteId: currentConstructionSite.id,
      },
      headers: restHeaders,
    },
    {
      refetchInterval: minutesToMilliseconds(
        DASHBOARD_REFRESH_TIMEOUT_IN_MINUTES,
      ),
    },
  );

  const loading = buildPerformanceLoading;
  const error = buildPerformanceError?.payload;

  if (error) {
    return { loading: false, error };
  }

  if (loading) {
    return { loading, error };
  }

  if (!buildPerformance?.scale) {
    return { loading, error, metrics: null };
  }

  const fastestExcavationFormatted = {
    duration: isUndefinedOrNull(
      buildPerformance?.durations?.excavation?.duration,
    )
      ? '-'
      : humanizeDuration(buildPerformance.durations.excavation.duration),
    ringNumber: t('dashboard.performance.ringNumber', {
      ringNumber: buildPerformance?.durations?.excavation?.ringNumber ?? '-',
    }),
  };

  const fastestRingPoseFormatted = {
    duration: isUndefinedOrNull(buildPerformance?.durations?.build?.duration)
      ? '-'
      : humanizeDuration(buildPerformance.durations.build.duration),
    ringNumber: t('dashboard.performance.ringNumber', {
      ringNumber: buildPerformance?.durations?.build?.ringNumber ?? '-',
    }),
  };

  const formatMonth = (date: string) => {
    return dayjs(date).format(MONTH_YEAR_FORMAT);
  };

  const formatWeek = (date: string) => {
    return t('common.dateRange', {
      startDate: dayjs(date).format(HUMAN_READABLE_FORMAT),
      endDate: dayjs(date).add(6, 'day').format(HUMAN_READABLE_FORMAT),
    });
  };

  const formatDay = (date: string) => {
    return dayjs(date).format(HUMAN_READABLE_FORMAT);
  };

  const metrics = {
    dayWithMostRingsBuilt: {
      count: buildPerformance.scale.day?.count ?? '-',
      date: buildPerformance.scale.day?.date
        ? formatDay(buildPerformance.scale.day.date)
        : '-',
    },
    weekWithMostRingsBuilt: {
      count: buildPerformance.scale.week?.count ?? '-',
      date: buildPerformance.scale.week?.date
        ? formatWeek(buildPerformance.scale.week.date)
        : '-',
    },
    monthWithMostRingsBuilt: {
      count: buildPerformance.scale.month?.count ?? '-',
      date: buildPerformance.scale.month?.date
        ? formatMonth(buildPerformance.scale.month.date)
        : '-',
    },
    fastestExcavation: fastestExcavationFormatted,
    fastestPose: fastestRingPoseFormatted,
  };

  return { loading, error, metrics };
};
